import { useRef, useState } from 'react';
import cx from 'clsx';

import MinusIcon from '~/assets/icons/ic__system__minus__outline.svg';
import PlusIcon from '~/assets/icons/ic__system__plus__outline.svg';

const INITIAL_VALUE = 1;
const DEFAULT_MIN_COUNT = 1;
const DEFAULT_MAX_COUNT = 999;
const DEFAULT_GAP = 1;
const DEFAULT_INTERVAL = 150;

type Props = {
  gap?: number;
  initialValue?: number;
  min?: number;
  max?: number;
  disabled?: boolean;
  onChange?(value: number): void;
  onFail?(message: string): void;
};
function Counter({
  gap = DEFAULT_GAP,
  initialValue = INITIAL_VALUE,
  min = DEFAULT_MIN_COUNT,
  max = DEFAULT_MAX_COUNT,
  disabled = false,
  onFail,
  onChange,
}: Props) {
  const [value, setValue] = useState(initialValue);

  const ref: { current: NodeJS.Timeout | null } = useRef(null);

  const onIncrease = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    event.stopPropagation();
    setValue((prev) => {
      const isMax = prev + gap > max;

      /**
       * 10개가 담겨 있는 상황에서
       * 주문 배수 단위가 3개인 경우
       * 10 > 12 > 15 ... 로 증가해야 한다.
       */

      if (isMax) {
        onFail?.(`최대 ${max}개 까지 구매할 수 있어요.`);
        return prev;
      }

      const next = prev + gap - (prev % gap);

      if (ref.current) {
        clearTimeout(ref.current);
      }

      ref.current = setTimeout(() => {
        onChange?.(next);
      }, DEFAULT_INTERVAL);

      return next;
    });
  };
  const onDecrease = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    event.stopPropagation();
    setValue((prev) => {
      const isMin = prev - gap < min;

      if (isMin) {
        onFail?.(`최소 ${min}개 이상부터 구매 가능해요.`);
        return prev;
      }

      const next = prev - gap;

      if (ref.current) {
        clearTimeout(ref.current);
      }

      ref.current = setTimeout(() => {
        onChange?.(next);
      }, DEFAULT_INTERVAL);

      return next;
    });
  };
  return (
    <div className="flex items-center rounded-full border border-screen-02 px-12 py-8">
      <button
        type="button"
        className={cx([
          'text-accent py-4 -m-4 px-8',
          disabled && 'pointer-events-none cursor-not-allowed',
        ])}
        onClick={onDecrease}
        disabled={disabled}
      >
        <MinusIcon />
      </button>
      <div className="min-w-[42px] text-center typo-title200-semibold">
        {value}
      </div>
      <button
        type="button"
        className={cx([
          'text-accent py-4 -m-4 px-8',
          disabled && 'pointer-events-none cursor-not-allowed',
        ])}
        onClick={onIncrease}
        disabled={disabled}
      >
        <PlusIcon />
      </button>
    </div>
  );
}

export default Counter;
